import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  History, 
  RotateCcw, 
  Clock, 
  User, 
  ChevronDown, 
  ChevronRight,
  Eye,
  AlertTriangle,
  Check,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { parseUtc } from '../../utils/utcTime';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NoteHistoryPanel - Shows version history for a note with restore capability
 */
export function NoteHistoryPanel({ 
  noteId, 
  isOpen, 
  onClose, 
  onRestoreComplete,
  isNoteLocked = false 
}) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showAutosaves, setShowAutosaves] = useState(true);

  // Fetch history when panel opens
  useEffect(() => {
    if (isOpen && noteId) {
      fetchHistory();
    }
  }, [isOpen, noteId]);

  const fetchHistory = async () => {
    if (!noteId) {
      console.error('[NoteHistory] No noteId provided');
      toast.error('No note selected');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      void 0 && console.log(`[NoteHistory] Fetching history for note: ${noteId}`);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${API}/notes/${noteId}/history?include_autosaves=${showAutosaves}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      void 0 && console.log('[NoteHistory] Response:', response.data);
      setHistory(response.data);
    } catch (error) {
      console.error('[NoteHistory] Error fetching note history:', error);
      console.error('[NoteHistory] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to load note history');
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific version data for preview
  const fetchVersionData = async (version) => {
    setLoadingVersion(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${API}/notes/${noteId}/history/${version}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setVersionData(response.data);
      setSelectedVersion(version);
    } catch (error) {
      console.error('Error fetching version data:', error);
      toast.error('Failed to load version details');
    } finally {
      setLoadingVersion(false);
    }
  };

  // Restore to a specific version
  const handleRestore = async () => {
    if (!selectedVersion) return;
    
    setRestoring(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API}/notes/${noteId}/restore/${selectedVersion}`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Note restored to version ${selectedVersion}`);
      // Jun 2026 — tell any open editor for this note to load the restored
      // content and sync its version, so a subsequent save can't overwrite
      // the restore with stale pre-restore form state.
      window.dispatchEvent(new CustomEvent('note-version-restored', {
        detail: {
          noteId,
          formData: versionData?.form_data || null,
          newVersion: response.data?.new_version || null,
        },
      }));
      setShowRestoreConfirm(false);
      setSelectedVersion(null);
      setVersionData(null);
      onRestoreComplete?.();
      onClose();
    } catch (error) {
      console.error('Error restoring note:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore note');
    } finally {
      setRestoring(false);
    }
  };

  // Get trigger badge styling
  const getTriggerBadge = (trigger, isAutosave) => {
    const styles = {
      'autosave': { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Auto-saved' },
      'field_blur': { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Field saved' },
      'manual': { bg: 'bg-green-50', text: 'text-green-600', label: 'Manual save' },
      'submit': { bg: 'bg-purple-50', text: 'text-purple-600', label: 'Submitted' },
      'sign': { bg: 'bg-teal-50', text: 'text-teal-600', label: 'Provider signed' },
      'supervisor_sign': { bg: 'bg-indigo-50', text: 'text-indigo-600', label: 'Supervisor signed' },
      'recovery': { bg: 'bg-orange-50', text: 'text-orange-600', label: 'Restored' },
      'migration': { bg: 'bg-slate-50', text: 'text-slate-600', label: 'Migrated' },
      'create': { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Created' },
    };
    
    const style = styles[trigger] || styles['autosave'];
    
    return (
      <Badge className={`${style.bg} ${style.text} text-xs`}>
        {style.label}
      </Badge>
    );
  };

  // Format date/time (timestamps from notes_history arrive without a tz
  // suffix — parse as UTC so they display in the user's local timezone)
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parseUtc(dateStr);
      if (!date) return String(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  // Render form data preview
  const renderFormDataPreview = (formData) => {
    if (!formData) return <p className="text-gray-500 italic">No content</p>;
    
    try {
      const data = typeof formData === 'string' ? JSON.parse(formData) : formData;
      
      // Handle sections-based structure
      if (data.sections && Array.isArray(data.sections)) {
        return (
          <div className="space-y-4">
            {data.sections.map((section, idx) => (
              <div key={idx} className="border-l-2 border-teal-200 pl-3">
                <h4 className="font-medium text-teal-700 mb-2">{section.title || `Section ${idx + 1}`}</h4>
                {section.fields && section.fields.length > 0 ? (
                  <div className="space-y-2">
                    {section.fields.map((field, fIdx) => {
                      const value = field.value || field.default_value;
                      if (!value || (Array.isArray(value) && value.length === 0)) return null;
                      const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                      return (
                        <div key={fIdx} className="text-sm bg-gray-50 p-2 rounded">
                          <div className="text-gray-600 font-medium text-xs mb-1">{field.label || field.name}</div>
                          <div className="text-gray-900 whitespace-pre-wrap select-all cursor-text">
                            {displayValue}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No fields</p>
                )}
              </div>
            ))}
          </div>
        );
      }
      
      // Handle flat fields structure
      if (data.fields && Array.isArray(data.fields)) {
        // Filter fields that have meaningful values
        const fieldsWithValues = data.fields.filter(field => {
          const value = field.value;
          // Skip if value is null, undefined, empty string, or empty array
          if (value === null || value === undefined) return false;
          if (value === '') return false;
          if (Array.isArray(value) && value.length === 0) return false;
          // Keep booleans and numbers
          return true;
        });
        
        if (fieldsWithValues.length === 0) {
          return <p className="text-gray-500 italic">No content filled in</p>;
        }
        
        return (
          <div className="space-y-2">
            {fieldsWithValues.map((field, idx) => {
              const value = field.value;
              const label = field.label || field.name || field.id || 'Field';
              
              let displayValue;
              if (typeof value === 'boolean') {
                displayValue = value ? 'Yes' : 'No';
              } else if (Array.isArray(value)) {
                displayValue = value.join(', ');
              } else {
                displayValue = String(value);
              }
              
              return (
                <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                  <div className="text-gray-600 font-medium text-xs mb-1">{label}</div>
                  <div className="text-gray-900 whitespace-pre-wrap select-all cursor-text">
                    {displayValue}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      
      // Fallback - show raw JSON preview
      return (
        <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40 select-all">
          {JSON.stringify(data, null, 2)}
        </pre>
      );
    } catch (e) {
      return <p className="text-gray-500 italic">Unable to parse content</p>;
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[500px] sm:w-[600px] p-0">
          <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-teal-50 to-blue-50">
            <SheetTitle className="flex items-center gap-2 text-teal-800">
              <History className="h-5 w-5" />
              Note Version History
            </SheetTitle>
            <SheetDescription>
              View previous versions and restore if needed. All changes are tracked.
            </SheetDescription>
          </SheetHeader>

          <div className="p-4 border-b bg-gray-50">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showAutosaves}
                onChange={(e) => {
                  setShowAutosaves(e.target.checked);
                  // Refetch with new setting
                  setTimeout(fetchHistory, 100);
                }}
                className="rounded border-gray-300"
              />
              <span className="text-gray-600">Show auto-saves</span>
            </label>
          </div>

          <ScrollArea className="h-[calc(100vh-200px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              </div>
            ) : !history || history.versions?.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No version history available</p>
                <p className="text-sm mt-1">History is created when you edit and save notes</p>
              </div>
            ) : (
              <div className="divide-y">
                {history.versions.map((version, idx) => (
                  <div
                    key={version.version}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedVersion === version.version ? 'bg-teal-50 border-l-4 border-teal-500' : ''
                    }`}
                    onClick={() => fetchVersionData(version.version)}
                    data-testid={`history-version-${version.version}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          Version {version.version}
                        </span>
                        {version.version === history.current_version && (
                          <Badge className="bg-teal-100 text-teal-700 text-xs">Current</Badge>
                        )}
                        {getTriggerBadge(version.save_trigger, version.is_autosave)}
                      </div>
                      {selectedVersion === version.version ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(version.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {version.created_by_name || 'Unknown'}
                      </span>
                    </div>
                    
                    {/* Expanded version details */}
                    {selectedVersion === version.version && versionData && (
                      <div className="mt-4 pt-4 border-t">
                        {loadingVersion ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
                          </div>
                        ) : (
                          <>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              Content Preview
                              <span className="text-xs text-gray-400 ml-2">(click to select, Ctrl/Cmd+C to copy)</span>
                            </h4>
                            <div className="bg-white border rounded-lg p-3 max-h-[400px] overflow-y-auto">
                              {renderFormDataPreview(versionData.form_data)}
                            </div>
                            
                            {/* Restore button */}
                            {version.version !== history.current_version && !isNoteLocked && (
                              <Button
                                size="sm"
                                className="mt-3 bg-teal-600 hover:bg-teal-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowRestoreConfirm(true);
                                }}
                                data-testid={`restore-version-${version.version}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Restore This Version
                              </Button>
                            )}
                            
                            {isNoteLocked && version.version !== history.current_version && (
                              <p className="mt-3 text-sm text-orange-600 flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                Cannot restore locked/signed notes
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-teal-600" />
              Restore to Version {selectedVersion}?
            </DialogTitle>
            <DialogDescription>
              This will restore the note content to version {selectedVersion}. 
              A new version will be created with the restored content - no data will be lost.
              The current version will still be available in history.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 inline mr-1" />
            Make sure to review the restored content after this operation.
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRestoreConfirm(false)}
              disabled={restoring}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoring}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-restore-btn"
            >
              {restoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Restore
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NoteHistoryPanel;
